import { Seo } from "../components/Seo";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Book,
  Code,
  Terminal,
  FileText,
  Search,
  ChevronRight,
  ExternalLink,
  Copy,
  CheckCircle2
} from "lucide-react";
import { useState } from "react";

const docSections = [
  {
    id: "getting-started",
    name: "Getting Started",
    icon: Book,
    items: [
      { title: "Quick Start", description: "Get up and running in 5 minutes", badge: "New" },
      { title: "Prerequisites", description: "System requirements and setup" },
      { title: "Authentication", description: "API keys and auth methods" },
      { title: "First Agent", description: "Register your first governed agent" }
    ]
  },
  {
    id: "api-reference",
    name: "API Reference",
    icon: Code,
    items: [
      { title: "Overview", description: "REST API fundamentals" },
      { title: "Agents", description: "Agent registration and management" },
      { title: "Policies", description: "Policy CRUD operations" },
      { title: "Actions", description: "Propose and execute actions" },
      { title: "Audit", description: "Audit trail queries" },
      { title: "Treasury", description: "Budget and distribution APIs" }
    ]
  },
  {
    id: "sdk",
    name: "SDK Guide",
    icon: Terminal,
    items: [
      { title: "Installation", description: "Python & TypeScript setup" },
      { title: "Configuration", description: "SDK options and settings" },
      { title: "Propose/Execute", description: "Action lifecycle" },
      { title: "Hooks", description: "Event callbacks and middleware" },
      { title: "Budget Context", description: "Budget-aware agent patterns" }
    ]
  },
  {
    id: "integrations",
    name: "Integrations",
    icon: FileText,
    items: [
      { title: "API Gateway", description: "Zero-code integration" },
      { title: "CrewAI", description: "CrewAI framework integration" },
      { title: "LangChain", description: "LangChain agent governance" },
      { title: "AutoGen", description: "Microsoft AutoGen support" },
      { title: "Custom Frameworks", description: "Build your own integration" }
    ]
  }
];

const codeExample = `from swarmcitadel import Agent, Policy

# Initialize your governed agent
agent = Agent(
    api_key="sk_live_...",
    name="trading-agent-01"
)

# Register with standard policy
agent.register(policy=Policy.STANDARD)

# Propose an action (requires approval)
proposal = await agent.propose(
    action="execute_trade",
    params={
        "symbol": "ETH/USD",
        "side": "buy",
        "amount": 1.5
    }
)

# Execute approved action
if proposal.approved:
    result = await agent.execute(proposal.id)
    print(f"Trade executed: {result.transaction_id}")`;

export const DocsPage = () => {
  const [copied, setCopied] = useState(false);

  const copyCode = () => {
    navigator.clipboard.writeText(codeExample);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="pt-24" data-testid="docs-page">
      <Seo title="Documentation | SwarmCitadel" description="Developer documentation for SwarmCitadel: API gateway, SDK integration, policy configuration and audit trail access for governed AI agents." path="/docs" />
      {/* Hero */}
      <section className="py-12 md:py-16 relative overflow-hidden border-b border-swarm-border">
        <div className="absolute inset-0 bg-mesh opacity-50" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="section-label">Documentation</div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                Developer Docs
              </h1>
              <p className="text-slate-400">
                Everything you need to integrate SwarmCitadel into your agent workflows.
              </p>
            </div>
            
            {/* Search */}
            <div className="relative w-full md:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Search documentation..."
                className="w-full pl-11 pr-4 py-3 bg-swarm-darker border border-swarm-border rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-swarm-violet"
                data-testid="docs-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-8 border-b border-swarm-border bg-swarm-darker/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-4">
            <Link to="#" className="flex items-center gap-2 px-4 py-2 bg-swarm-violet/10 text-swarm-violet rounded-lg hover:bg-swarm-violet/20 transition-colors">
              <Terminal className="w-4 h-4" />
              Quick Start
            </Link>
            <Link to="#" className="flex items-center gap-2 px-4 py-2 bg-white/5 text-slate-300 rounded-lg hover:bg-white/10 transition-colors">
              <Code className="w-4 h-4" />
              API Reference
            </Link>
            <Link to="#" className="flex items-center gap-2 px-4 py-2 bg-white/5 text-slate-300 rounded-lg hover:bg-white/10 transition-colors">
              <ExternalLink className="w-4 h-4" />
              SDK on GitHub
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="glass-card p-6 sticky top-28">
                <h3 className="text-lg font-semibold text-white mb-4">Documentation</h3>
                <nav className="space-y-6">
                  {docSections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <div key={section.id}>
                        <div className="flex items-center gap-2 text-slate-400 mb-3">
                          <Icon className="w-4 h-4" />
                          <span className="text-sm font-medium">{section.name}</span>
                        </div>
                        <ul className="space-y-1 ml-6">
                          {section.items.map((item) => (
                            <li key={item.title}>
                              <Link
                                to="#"
                                className="block py-1 text-sm text-slate-500 hover:text-swarm-cyan transition-colors"
                              >
                                {item.title}
                                {item.badge && (
                                  <span className="ml-2 px-2 py-0.5 text-xs bg-swarm-green/20 text-swarm-green rounded">
                                    {item.badge}
                                  </span>
                                )}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-2">
              {/* Quick Start */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-4">Quick Start</h2>
                <p className="text-slate-400 mb-6">
                  Get your first agent governed in under 5 minutes with our Python SDK.
                </p>

                {/* Installation */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-white mb-3">1. Install the SDK</h3>
                  <div className="bg-swarm-darker rounded-lg p-4 font-mono text-sm">
                    <code className="text-swarm-cyan">pip install swarmcitadel</code>
                  </div>
                </div>

                {/* Code Example */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">2. Register and Execute</h3>
                  <div className="relative">
                    <button
                      onClick={copyCode}
                      className="absolute top-4 right-4 p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                      data-testid="copy-code-btn"
                    >
                      {copied ? (
                        <CheckCircle2 className="w-4 h-4 text-swarm-green" />
                      ) : (
                        <Copy className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                    <div className="bg-swarm-darker rounded-lg p-6 font-mono text-sm overflow-x-auto">
                      <pre className="text-slate-300">
                        <code>{codeExample}</code>
                      </pre>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sections Grid */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">Explore Documentation</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {docSections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <Link
                        key={section.id}
                        to="#"
                        className="glass-card-hover p-6 group"
                        data-testid={`doc-section-${section.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="p-3 rounded-lg bg-swarm-violet/10 text-swarm-violet">
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-white font-semibold mb-1 group-hover:text-swarm-cyan transition-colors">
                              {section.name}
                            </h3>
                            <p className="text-slate-500 text-sm">
                              {section.items.length} articles
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-swarm-cyan transition-colors" />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* API Endpoints Preview */}
              <div className="mt-12">
                <h2 className="text-2xl font-bold text-white mb-6">API Endpoints</h2>
                <div className="glass-card overflow-hidden">
                  <div className="p-4 border-b border-swarm-border bg-swarm-darker/50">
                    <span className="font-mono text-sm text-slate-400">150 REST Endpoints</span>
                  </div>
                  <div className="divide-y divide-swarm-border">
                    {[
                      { method: "POST", path: "/agents", description: "Register a new agent" },
                      { method: "GET", path: "/agents/:id", description: "Get agent details" },
                      { method: "POST", path: "/actions/propose", description: "Propose an action" },
                      { method: "POST", path: "/actions/:id/execute", description: "Execute approved action" },
                      { method: "GET", path: "/audit", description: "Query audit trail" }
                    ].map((endpoint) => (
                      <div key={endpoint.path} className="flex items-center gap-4 p-4 hover:bg-white/5">
                        <span className={`font-mono text-xs px-2 py-1 rounded ${
                          endpoint.method === 'POST' 
                            ? 'bg-swarm-green/20 text-swarm-green' 
                            : 'bg-swarm-cyan/20 text-swarm-cyan'
                        }`}>
                          {endpoint.method}
                        </span>
                        <code className="font-mono text-sm text-white">{endpoint.path}</code>
                        <span className="text-slate-500 text-sm ml-auto">{endpoint.description}</span>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 border-t border-swarm-border">
                    <Link to="#" className="text-swarm-cyan text-sm hover:underline flex items-center gap-1">
                      View all endpoints <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-swarm-darker/50 border-t border-swarm-border">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Need help getting started?
          </h2>
          <p className="text-slate-400 mb-6">
            Our team is here to help you integrate SwarmCitadel into your workflows.
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-swarm-violet hover:bg-swarm-violet/90">
                Contact Support
              </Button>
            </Link>
            <Button variant="outline" className="btn-outline">
              Join Discord
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DocsPage;
